import AxiosInstance from '../config/AxiosInstance';

export interface CashupShiftOption {
  userShiftId: string;
  deviceSessionId: string;
  deviceUserId: string;
  userName: string;
  deviceName: string | null;
  sessionNumber: string | null;
  startDate: string;
  endDate: string | null;
  open: boolean;
}

export interface CashupSessionOption {
  deviceSessionId: string;
  sessionNumber: string | null;
  deviceName: string | null;
  startDate: string;
  endDate: string | null;
  userCount: number;
  open: boolean;
}

export interface CashupSummary {
  totalSales: number;
  totalDiscounts: number;
  totalRefunds: number;
  totalVoids: number;
  netSales: number;
  txnCount: number;
  itemsSold: number;
  avgPerTxn: number;
  // Optional until inventory-api feat/cashup-xz-totals is deployed.
  totalTax?: number;
  serviceCharge?: number;
  tip?: number;
  covers?: number;
}

export interface CashupTaxRow {
  taxId: string | null;
  code: string;
  name: string;
  rate: number | null;
  taxable: number;
  tax: number;
}

export interface CashupReportData {
  scope: 'shift' | 'session';
  open: boolean;
  userName?: string;
  deviceName: string | null;
  sessionNumber: string | null;
  startDate: string;
  endDate: string | null;
  declarationNumber?: string | null;
  float: number;
  summary: CashupSummary;
  /** VAT in the UK, TAX elsewhere. */
  taxLabel?: 'VAT' | 'TAX';
  taxBreakdown?: CashupTaxRow[];
  tenders: {
    deviceTenderId: string | null;
    tenderName: string;
    tenderType: number | null;
    amount: number;
  }[];
  cashDeclaration: {
    float: number;
    cashMovement: number;
    systemAmount: number;
    declaredAmount: number | null;
    variance: number | null;
  };
  declarations: {
    tenderName: string;
    systemAmount: number;
    declaredAmount: number;
    variance: number;
  }[];
  byUser?: { userShiftId: string; userName: string; totalSales: number }[];
  /** Z only: session sales not attributable to any user shift. */
  unassignedSales?: number;
  parity?: { matches: boolean; deltas: Record<string, number> } | null;
  freshness: {
    pendingTotal: number;
    staleTills: number;
    tillsTotal: number;
    generatedAt: string;
  };
}

const BASE = '/api/inventory/reports/cashup';

// The day runs from local midnight in the browser's zone, as the finance reports count days.
const getCashupDay = async (date: string, locationId?: string) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return AxiosInstance.get(`${BASE}/day`, {
    params: locationId ? { date, locationId, tz } : { date, tz },
  });
};

const getXReport = async (userShiftId: string) =>
  AxiosInstance.get(`${BASE}/x/${userShiftId}`);

const getZReport = async (deviceSessionId: string) =>
  AxiosInstance.get(`${BASE}/z/${deviceSessionId}`);

export { getCashupDay, getXReport, getZReport };
